// hooks/useGazette.ts
import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { GazetteService } from '../services/gazette.service';
import { GazetteAnnouncement, GazetteFilters } from '../types/gazette.types';

const gazetteService = GazetteService.getInstance();

export function useGazette(initialFilters?: Partial<GazetteFilters>) {
  const [announcements, setAnnouncements] = useState<GazetteAnnouncement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [refreshing, setRefreshing] = useState(false);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState<string>('');
  const [selectedOffice, setSelectedOffice] = useState<string>('');
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [openOnly, setOpenOnly] = useState(false);

  const isLoadingRef = useRef(false);
  const initialLoadDone = useRef(false);

  // Build filters object
  const filters = useMemo<GazetteFilters>(() => ({
    page,
    type: selectedType || undefined,
    office: selectedOffice || undefined,
    category: selectedCategory || undefined,
    startDate: startDate || undefined,
    endDate: endDate || undefined,
    openOnly: openOnly || undefined,
    searchTerm: searchTerm || undefined,
  }), [page, selectedType, selectedOffice, selectedCategory, startDate, endDate, openOnly, searchTerm]);

  const loadAnnouncements = useCallback(async (reset = false) => {
    if (isLoadingRef.current) return;

    try {
      isLoadingRef.current = true;
      setLoading(true);
      setError(null);

      const currentPage = reset ? 1 : page;
      const updatedFilters = { ...filters, page: currentPage };

      const response = await gazetteService.fetchAnnouncements(updatedFilters);

      if (response.success) {
        if (reset) {
          setAnnouncements(response.data);
          setPage(1);
        } else {
          setAnnouncements((prev) => {
            // Avoid duplicates by using link as unique key
            const existingLinks = new Set(prev.map(item => item.link));
            const newItems = response.data.filter(item => !existingLinks.has(item.link));
            return [...prev, ...newItems];
          });
          setPage(currentPage + 1);
        }
        setHasMore(response.hasMore);
        setError(null);
      } else {
        setError(response.error || 'Failed to load announcements');
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
      setRefreshing(false);
    }
  }, [page, filters]);

  // Initial load and filter changes
  useEffect(() => {
    // Reset announcements when filters change
    if (initialLoadDone.current) {
      setAnnouncements([]);
      setPage(1);
      loadAnnouncements(true);
    } else {
      loadAnnouncements(true);
      initialLoadDone.current = true;
    }
  }, [selectedType, selectedOffice, selectedCategory, startDate, endDate, openOnly, searchTerm]);

  const refresh = useCallback(() => {
    setRefreshing(true);
    setAnnouncements([]);
    setPage(1);
    loadAnnouncements(true);
  }, [loadAnnouncements]);

  const loadMore = useCallback(() => {
    if (!loading && hasMore && !isLoadingRef.current) {
      loadAnnouncements(false);
    }
  }, [loading, hasMore, loadAnnouncements]);

  const resetFilters = useCallback(() => {
    setSearchTerm('');
    setSelectedType('');
    setSelectedOffice('');
    setSelectedCategory('');
    setStartDate('');
    setEndDate('');
    setOpenOnly(false);
    setAnnouncements([]);
    setPage(1);
    initialLoadDone.current = false;
  }, []);

  return {
    announcements,
    loading,
    error,
    hasMore,
    refreshing,
    loadMore,
    refresh,
    filters,
    resetFilters,
    searchTerm,
    setSearchTerm,
    selectedType,
    setSelectedType,
    selectedOffice,
    setSelectedOffice,
    selectedCategory,
    setSelectedCategory,
    startDate,
    setStartDate,
    endDate,
    setEndDate,
    openOnly,
    setOpenOnly,
  };
}